import { Vector2 } from "../../common/vector2.js";
import { Rect } from "../../common/rect.js";
import { UIElement } from "./ui_element.js";
import { Game } from "../../game.js";

export interface IScrollableContainerCSSOptions
{
  scroll_speed?: number;
  momentum_friction?: number;
  enable_momentum?: boolean;
  enable_touch_scroll?: boolean;
  scroll_sensitivity?: number;
  enable_rubber_band?: boolean;
  rubber_band_resistance?: number;
  rubber_band_max_distance?: number;
  rubber_band_progression?: number;
  clip_horizontal_overflow?: number;
  /** Before the first vertical scroll gesture, shrink the clip height by this many CSS pixels; full height after. */
  pre_scroll_viewport_shrink?: number;
  /**
   * If true (and shrink > 0), do not expand the viewport on the first vertical drag; call
   * `expandPreScrollViewportIfNeeded()` from outside (e.g. when a floating row starts tracking scroll).
   */
  pre_scroll_defer_expand_until_external?: boolean;
  /** Cap the DPR used for the content canvas (default: device DPR). Useful to limit memory on high-DPR screens. */
  content_dpr_cap?: number;
  window_buffer?: number;
}

export class UIScrollableContainerCSS extends UIElement
{
  private clip_div!: HTMLDivElement;
  private content_canvas!: HTMLCanvasElement;
  private content_ctx!: CanvasRenderingContext2D;

  private viewport_rect!: Rect;
  private content_height: number = 0;
  private scroll_offset: number = 0;
  private max_scroll_offset: number = 0;
  private is_over_scrolling: boolean = false;

  private options: Required<IScrollableContainerCSSOptions>;
  private rubber_band_exp_factor: number = 0;

  private scroll_velocity: number = 0;
  private touch_start_pos: Vector2 | null = null;
  private touch_last_pos: Vector2 | null = null;
  private touch_last_time: number = 0;
  private is_scrolling: boolean = false;
  private swipe_direction: "up" | "down" | null = null;

  private snap_targets: number[] = [];
  private scroll_end_callback?: () => void;
  private current_snap_section: number = -1;
  private snap_section_change_callback?: ( new_section: number ) => void;
  private last_update_time: number | null = null;
  private is_raf_animating: boolean = false;
  private raf_id: number = 0;

  private last_transform_str: string = '';
  private log_friction: number = 0;
  private velocity_samples: number[] = [];
  private static readonly VELOCITY_WINDOW = 4;

  private clip_h_overflow: number = 0;

  private full_viewport_height: number = 0;
  private pre_scroll_shrink: number = 0;
  private viewport_collapsed: boolean = false;

  private window_top: number = 0;
  private window_drawn: boolean = false;
  private window_renderer: ( ( top: number, bottom: number ) => void ) | null = null;
  private scratch_canvas: HTMLCanvasElement | null = null;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    options: IScrollableContainerCSSOptions = {}
  )
  {
    super( ctx, x, y, width, height );

    this.options = {
      scroll_speed:              options.scroll_speed              ?? 1.0,
      momentum_friction:         options.momentum_friction         ?? 0.95,
      enable_momentum:           options.enable_momentum           ?? true,
      enable_touch_scroll:       options.enable_touch_scroll       ?? true,
      scroll_sensitivity:        options.scroll_sensitivity        ?? 1.0,
      enable_rubber_band:        options.enable_rubber_band        ?? true,
      rubber_band_resistance:    options.rubber_band_resistance    ?? 0.3,
      rubber_band_max_distance:  options.rubber_band_max_distance  ?? 100,
      rubber_band_progression:   options.rubber_band_progression   ?? 3.5,
      clip_horizontal_overflow:  options.clip_horizontal_overflow  ?? 0,
      pre_scroll_viewport_shrink: options.pre_scroll_viewport_shrink ?? 0,
      pre_scroll_defer_expand_until_external: options.pre_scroll_defer_expand_until_external ?? false,
      content_dpr_cap:           options.content_dpr_cap           ?? Infinity,
      window_buffer:             options.window_buffer             ?? 0,
    };

    this.rubber_band_exp_factor = this.options.rubber_band_progression * Math.LN2;
    this.log_friction            = Math.log( this.options.momentum_friction );
    this.clip_h_overflow = this.options.clip_horizontal_overflow;

    this.full_viewport_height = height;
    this.pre_scroll_shrink    = this.options.pre_scroll_viewport_shrink;
    this.viewport_collapsed   = this.pre_scroll_shrink > 0;
    const initial_viewport_h = this.viewport_collapsed
      ? Math.max( 1, height - this.pre_scroll_shrink )
      : height;
    this.viewport_rect = new Rect( 0, 0, width, initial_viewport_h );

    this.setupDOM( width, initial_viewport_h );
  }

  private setupDOM( width: number, height: number ): void
  {
    this.clip_div = document.createElement( "div" );
    this.clip_div.style.cssText = [
      "position:fixed",
      "left:0px",
      "top:0px",
      `width:${width + this.clip_h_overflow * 2}px`,
      `height:${height}px`,
      "overflow:hidden",
      "pointer-events:none",
      "z-index:10",
    ].join( ";" );

    const dpr = this.getContentDpr();
    this.content_canvas = document.createElement( "canvas" );
    this.content_canvas.width  = Math.ceil( ( width + this.clip_h_overflow * 2 ) * dpr );
    this.content_canvas.height = Math.ceil( dpr );
    this.content_canvas.style.cssText = [
      "position:absolute",
      "left:0",
      "top:0",
      `width:${width + this.clip_h_overflow * 2}px`,
      "height:0px",
      "will-change:transform",
      "pointer-events:none",
    ].join( ";" );

    this.clip_div.appendChild( this.content_canvas );
    document.body.appendChild( this.clip_div );

    this.content_ctx = this.content_canvas.getContext( "2d" )!;
    this.applyContentCtxBaseTransform();
  }

  private getContentDpr(): number
  {
    return Math.min( Game.getDpr(), this.options.content_dpr_cap );
  }

  private applyContentCtxBaseTransform(): void
  {
    const dpr = this.getContentDpr();
    this.content_ctx.setTransform( dpr, 0, 0, dpr, this.clip_h_overflow * dpr, -this.window_top * dpr );
  }

  private isWindowed(): boolean
  {
    return this.options.window_buffer > 0;
  }

  private windowBuffer(): number
  {
    return this.full_viewport_height * this.options.window_buffer;
  }

  private windowHeight(): number
  {
    return Math.min( this.content_height, this.full_viewport_height + this.windowBuffer() * 2 );
  }

  public setWindowRenderer( renderer: ( top: number, bottom: number ) => void ): void
  {
    this.window_renderer = renderer;
  }

  private syncWindow(): void
  {
    const window_h = this.windowHeight();
    if ( window_h <= 0 )
      return;

    const view_top    = Math.max( 0, this.scroll_offset );
    const view_bottom = Math.min( this.content_height, this.scroll_offset + this.viewport_rect.getH() );
    const window_bottom = this.window_top + window_h;
    if ( this.window_drawn && view_top >= this.window_top && view_bottom <= window_bottom )
      return;

    const dpr      = this.getContentDpr();
    const canvas_h = this.content_canvas.height;
    const max_top  = Math.max( 0, this.content_height - window_h );
    const new_top  = Math.round( Math.max( 0, Math.min( this.scroll_offset - this.windowBuffer(), max_top ) ) * dpr ) / dpr;
    const shift_px = Math.round( ( new_top - this.window_top ) * dpr );

    if ( this.window_drawn && Math.abs( shift_px ) < canvas_h - 1 )
    {
      const scratch = this.scratchCanvas();
      const scratch_ctx = scratch.getContext( "2d" )!;
      scratch_ctx.clearRect( 0, 0, scratch.width, scratch.height );
      scratch_ctx.drawImage( this.content_canvas, 0, 0 );
      this.content_ctx.save();
      this.content_ctx.setTransform( 1, 0, 0, 1, 0, 0 );
      this.content_ctx.clearRect( 0, 0, this.content_canvas.width, canvas_h );
      this.content_ctx.drawImage( scratch, 0, -shift_px );
      this.content_ctx.restore();
      this.window_top = new_top;
      this.applyContentCtxBaseTransform();
      if ( shift_px > 0 )
        this.renderWindowStrip( canvas_h - shift_px - 1, canvas_h );
      else if ( shift_px < 0 )
        this.renderWindowStrip( 0, 1 - shift_px );
    }
    else
    {
      this.window_top = new_top;
      this.applyContentCtxBaseTransform();
      this.renderWindowStrip( 0, canvas_h );
    }

    this.window_drawn = true;
  }

  private scratchCanvas(): HTMLCanvasElement
  {
    if ( !this.scratch_canvas )
      this.scratch_canvas = document.createElement( "canvas" );

    if ( this.scratch_canvas.width !== this.content_canvas.width || this.scratch_canvas.height !== this.content_canvas.height )
    {
      this.scratch_canvas.width  = this.content_canvas.width;
      this.scratch_canvas.height = this.content_canvas.height;
    }

    return this.scratch_canvas;
  }

  private renderWindowStrip( top_px: number, bottom_px: number ): void
  {
    const from_px = Math.max( 0, top_px );
    const to_px   = Math.min( this.content_canvas.height, bottom_px );
    if ( to_px <= from_px )
      return;

    const dpr = this.getContentDpr();
    const w_px = this.content_canvas.width;
    this.content_ctx.save();
    this.content_ctx.setTransform( 1, 0, 0, 1, 0, 0 );
    this.content_ctx.beginPath();
    this.content_ctx.rect( 0, from_px, w_px, to_px - from_px );
    this.content_ctx.clip();
    this.content_ctx.clearRect( 0, from_px, w_px, to_px - from_px );
    this.applyContentCtxBaseTransform();
    this.window_renderer?.( this.window_top + from_px / dpr, this.window_top + to_px / dpr );
    this.content_ctx.restore();
  }

  public updateDOMPosition(): void
  {
    const abs  = this.getAbsolutePosition();
    const h_ov = this.clip_h_overflow;
    this.clip_div.style.left = `${Game.getGameX() + abs.x - h_ov}px`;
    this.clip_div.style.top  = `${abs.y}px`;
  }


  public getContentCtx(): CanvasRenderingContext2D
  {
    return this.content_ctx;
  }

  public getOverlayHost(): HTMLDivElement
  {
    return this.clip_div;
  }

  public setContentHeight( height: number ): void
  {
    this.content_height = height;

    const dpr      = this.getContentDpr();
    const w        = this.viewport_rect.getW() + this.clip_h_overflow * 2;
    const canvas_h = this.isWindowed() ? this.windowHeight() : height;
    this.content_canvas.width  = Math.ceil( w * dpr );
    this.content_canvas.height = Math.max( 1, Math.ceil( canvas_h * dpr ) );
    this.content_canvas.style.height = `${canvas_h}px`;

    this.window_top   = 0;
    this.window_drawn = false;
    this.applyContentCtxBaseTransform();

    this.recomputeScrollLimits();
  }

  private recomputeScrollLimits(): void
  {
    this.max_scroll_offset = this.content_height > 0
      ? Math.max( 0, this.content_height - this.viewport_rect.getH() )
      : 0;
    this.scroll_offset = Math.max( 0, Math.min( this.scroll_offset, this.max_scroll_offset ) );
    if ( this.snap_targets.length > 0 )
      this.snap_targets = this.snap_targets.map( ( t ) => Math.max( 0, Math.min( t, this.max_scroll_offset ) ) );
    this.applyScrollTransform();
    this.checkSnapSectionChange();
  }

  private expandViewportIfCollapsed(): void
  {
    if ( !this.viewport_collapsed ) return;
    this.viewport_collapsed = false;
    this.viewport_rect = new Rect( 0, 0, this.viewport_rect.getW(), this.full_viewport_height );
    this.clip_div.style.height = `${this.full_viewport_height}px`;
    this.recomputeScrollLimits();
  }

  /** Expands a collapsed pre-scroll viewport once; no-op if already full size or shrink is 0. */
  public expandPreScrollViewportIfNeeded(): void
  {
    this.expandViewportIfCollapsed();
  }

  /** Shrinks back by `pre_scroll_viewport_shrink` after expand; no-op if shrink is 0 or already collapsed. */
  public collapsePreScrollViewportIfNeeded(): void
  {
    if ( this.pre_scroll_shrink <= 0 || this.viewport_collapsed ) return;
    this.viewport_collapsed = true;
    const vh = Math.max( 1, this.full_viewport_height - this.pre_scroll_shrink );
    this.viewport_rect = new Rect( 0, 0, this.viewport_rect.getW(), vh );
    this.clip_div.style.height = `${vh}px`;
    this.recomputeScrollLimits();
  }

  public clearContent(): void
  {
    this.content_ctx.save();
    this.content_ctx.setTransform( 1, 0, 0, 1, 0, 0 );
    this.content_ctx.clearRect( 0, 0, this.content_canvas.width, this.content_canvas.height );
    this.content_ctx.restore();
  }

  public clearScrollableChildren(): void
  {
    cancelAnimationFrame( this.raf_id );
    this.scroll_velocity   = 0;
    this.scroll_offset     = 0;
    this.max_scroll_offset = 0;
    this.content_height    = 0;
    this.is_raf_animating  = false;
    this.velocity_samples  = [];
    this.last_transform_str = '';
    this.collapsePreScrollViewportIfNeeded();
    this.clearContent();
    this.applyScrollTransform();
  }

  public getScrollOffset(): number
  {
    return this.scroll_offset;
  }

  public getMaxScrollOffset(): number
  {
    return this.max_scroll_offset;
  }

  public setOnScrollEnd( callback: () => void ): void
  {
    this.scroll_end_callback = callback;
  }

  public setSnapTargets( targets: number[] ): void
  {
    this.snap_targets = targets.map( ( t ) => Math.max( 0, Math.min( t, this.max_scroll_offset ) ) );
  }

  public setOnSnapSectionChange( callback: ( new_section: number ) => void ): void
  {
    this.snap_section_change_callback = callback;
  }

  public scrollTo( offset: number, animate: boolean = true ): void
  {
    this.expandViewportIfCollapsed();
    const clamped = Math.max( 0, Math.min( offset, this.max_scroll_offset ) );
    if ( animate )
    {
      this.animateScrollTo( clamped );
    }
    else
    {
      this.scroll_offset = clamped;
      this.applyScrollTransform();
      this.checkSnapSectionChange();
      if ( this.scroll_end_callback ) this.scroll_end_callback();
    }
  }

  public getCurrentSnapSection(): number
  {
    const current = this.scroll_offset;
    if ( this.snap_targets.length === 0 ) return 0;
    if ( current < this.snap_targets[0] ) return 0;
    for ( let i = 0; i < this.snap_targets.length - 1; i++ )
    {
      if ( current >= this.snap_targets[i] && current < this.snap_targets[i + 1] )
        return i;
    }
    return this.snap_targets.length - 1;
  }

  public hideElement( duration: number = 300 ): void
  {
    // DOM-only fade: a canvas-side opacity transform would dirty the parent
    // tree every frame while this element draws nothing on the main canvas.
    this.clip_div.style.transition = duration > 16 ? `opacity ${duration}ms ease-out` : "";
    this.clip_div.style.opacity = "0";
    if ( duration <= 16 )
      this.clip_div.style.display = "none";
  }

  public showElement( duration: number = 300 ): void
  {
    this.clip_div.style.display = "";
    if ( duration > 16 )
    {
      this.clip_div.style.transition = "";
      this.clip_div.style.opacity = "0";
      requestAnimationFrame( () =>
      {
        this.clip_div.style.transition = `opacity ${duration}ms ease-in`;
        this.clip_div.style.opacity = "1";
      } );
    }
    else
    {
      this.clip_div.style.transition = "";
      this.clip_div.style.opacity = "1";
    }
  }

  public destroy(): void
  {
    cancelAnimationFrame( this.raf_id );
    this.scratch_canvas = null;
    if ( this.clip_div.parentNode )
    {
      this.clip_div.parentNode.removeChild( this.clip_div );
    }
  }


  private applyScrollTransform(): void
  {
    if ( this.isWindowed() )
      this.syncWindow();

    const val = `translate3d(0,${this.window_top - this.scroll_offset}px,0)`;
    if ( val === this.last_transform_str ) return;
    this.last_transform_str = val;
    this.content_canvas.style.transform = val;
  }

  private updateScrollOffset( new_offset: number ): void
  {
    const clamped = Math.max( 0, Math.min( new_offset, this.max_scroll_offset ) );
    if ( clamped !== this.scroll_offset )
    {
      this.scroll_offset = clamped;
      this.applyScrollTransform();
      this.checkSnapSectionChange();
    }
  }

  private updateScrollOffsetRubberBand( new_offset: number ): void
  {
    this.is_over_scrolling = new_offset < 0 || new_offset > this.max_scroll_offset;
    if ( new_offset !== this.scroll_offset )
    {
      this.scroll_offset = new_offset;
      this.applyScrollTransform();
      this.checkSnapSectionChange();
    }
  }

  private checkSnapSectionChange(): void
  {
    const new_section = this.getCurrentSnapSection();
    if ( new_section !== this.current_snap_section )
    {
      this.current_snap_section = new_section;
      if ( this.snap_section_change_callback )
        this.snap_section_change_callback( new_section );
    }
  }

  private applyRubberBandEffect( delta: number ): number
  {
    if ( !this.options.enable_rubber_band ) return delta;

    const new_offset = this.scroll_offset + delta;
    let distance_beyond: number;

    if ( new_offset < 0 )
      distance_beyond = -new_offset;
    else if ( new_offset > this.max_scroll_offset )
      distance_beyond = new_offset - this.max_scroll_offset;
    else
      return delta;

    const max_distance = this.options.rubber_band_max_distance;
    if ( distance_beyond > max_distance ) return delta * 0.05;

    const progress = distance_beyond / max_distance;
    const geometric_factor = Math.exp( progress * this.rubber_band_exp_factor );
    const resistance = 1 - ( 1 / geometric_factor );

    return delta * ( 1 - resistance * this.options.rubber_band_resistance );
  }

  private snapBackToBounds(): void
  {
    if ( !this.options.enable_rubber_band ) return;

    let target = this.scroll_offset;
    if ( this.scroll_offset < 0 ) target = 0;
    else if ( this.scroll_offset > this.max_scroll_offset ) target = this.max_scroll_offset;

    if ( target !== this.scroll_offset )
    {
      this.is_over_scrolling = false;
      this.animateScrollTo( target );
    }
  }

  private snapToNearestTarget( direction: "up" | "down" | null ): void
  {
    if ( this.snap_targets.length === 0 ) return;

    const current = this.scroll_offset;

    for ( let i = 0; i < this.snap_targets.length; i++ )
    {
      const start = this.snap_targets[i];
      const end   = this.snap_targets[i + 1] ?? this.max_scroll_offset;
      const range = end - start;

      if ( current >= start && current < end )
      {
        let threshold: number;
        if ( direction === "down" )      threshold = start + range * ( 2 / 3 );
        else if ( direction === "up" )   threshold = start + range * ( 1 / 3 );
        else                             threshold = start + range * 0.5;

        this.scrollTo( current < threshold ? start : end, true );
        return;
      }
    }

    const closest = this.snap_targets.reduce( ( a, b ) =>
      Math.abs( b - current ) < Math.abs( a - current ) ? b : a
    );
    this.scrollTo( closest, true );
  }

  private animateScrollTo( target: number ): void
  {
    cancelAnimationFrame( this.raf_id );

    if ( Math.abs( target - this.scroll_offset ) < 1 )
    {
      this.scroll_offset    = target;
      this.is_raf_animating = false;
      this.applyScrollTransform();
      this.checkSnapSectionChange();
      if ( this.scroll_end_callback ) this.scroll_end_callback();
      return;
    }

    const start    = this.scroll_offset;
    const delta    = target - start;
    const duration = 300;
    const t0       = performance.now();
    this.is_raf_animating = true;

    const tick = ( now: number ) =>
    {
      const t    = Math.min( 1, ( now - t0 ) / duration );
      const ease = t * ( 2 - t );
      this.scroll_offset = start + delta * ease;
      this.applyScrollTransform();

      if ( t < 1 )
      {
        this.raf_id = requestAnimationFrame( tick );
      }
      else
      {
        this.scroll_offset    = target;
        this.is_raf_animating = false;
        this.applyScrollTransform();
        this.checkSnapSectionChange();
        if ( this.scroll_end_callback ) this.scroll_end_callback();
      }
    };

    this.raf_id = requestAnimationFrame( tick );
  }


  private isInViewport( p: Vector2 ): boolean
  {
    const local_p = this.toLocalCoordinates( p );
    return this.viewport_rect.contains( local_p.x, local_p.y );
  }

  private toLocalCoordinates( p: Vector2 ): Vector2
  {
    const pixel_pos = this.pixelPosForRender();
    return new Vector2( p.x - pixel_pos.x, p.y - pixel_pos.y );
  }

  public onTouchStart( p: Vector2, now: number ): boolean
  {
    this.touch_start_pos  = p.clone();
    this.touch_last_pos   = p.clone();
    this.touch_last_time  = now;
    this.is_scrolling     = false;
    this.scroll_velocity  = 0;
    this.velocity_samples = [];
    this.last_update_time = now;

    const handled = super.onTouchStart( p, now );
    if ( handled || this.isInViewport( p ) )
      return true;

    return false;
  }

  public onTouchMove( p: Vector2, now: number ): void
  {
    if ( this.touch_start_pos && this.options.enable_touch_scroll )
    {
      const delta      = p.subtract( this.touch_last_pos! );
      const time_delta = now - this.touch_last_time;

      if ( !this.is_scrolling )
      {
        const total_delta    = p.subtract( this.touch_start_pos );
        const scroll_threshold = 6;
        if ( Math.abs( total_delta.y ) > scroll_threshold )
        {
          this.is_scrolling = true;
          if ( !this.options.pre_scroll_defer_expand_until_external )
            this.expandViewportIfCollapsed();
        }
      }

      if ( this.is_scrolling )
      {
        const raw_delta      = -delta.y * this.options.scroll_sensitivity;
        const adjusted_delta = this.applyRubberBandEffect( raw_delta );
        const new_offset     = this.scroll_offset + adjusted_delta;

        if ( this.options.enable_rubber_band )
          this.updateScrollOffsetRubberBand( new_offset );
        else
          this.updateScrollOffset( new_offset );

        if ( time_delta > 0 )
        {
          const raw_v = -delta.y / time_delta;
          this.velocity_samples.push( raw_v );
          if ( this.velocity_samples.length > UIScrollableContainerCSS.VELOCITY_WINDOW )
            this.velocity_samples.shift();
          let weighted = 0, total_w = 0;
          for ( let i = 0; i < this.velocity_samples.length; i++ )
          {
            const w = i + 1;
            weighted += this.velocity_samples[i] * w;
            total_w  += w;
          }
          this.scroll_velocity = weighted / total_w;
        }

        this.touch_last_pos  = p.clone();
        this.touch_last_time = now;
        return;
      }
    }

    super.onTouchMove( p, now );
  }

  public onTouchEnd( p: Vector2, now: number, p_global: Vector2 | null = null ): void
  {
    const was_scrolling = this.is_scrolling;

    if ( this.touch_start_pos && this.touch_last_pos )
    {
      const deltaY = this.touch_last_pos.y - this.touch_start_pos.y;
      this.swipe_direction = Math.abs( deltaY ) > 10
        ? ( deltaY > 0 ? "down" : "up" )
        : null;
    }

    if ( this.snap_targets.length > 0 && !this.is_over_scrolling )
    {
      requestAnimationFrame( () => { this.snapToNearestTarget( this.swipe_direction ); } );
    }
    else
    {
      this.snapBackToBounds();
    }

    this.touch_start_pos = null;
    this.is_scrolling    = false;

    if ( !was_scrolling )
    {
      super.onTouchEnd( p, now, p_global );
      return;
    }

    const cancel_point = new Vector2( -1000000, -1000000 );
    super.onTouchEnd( cancel_point, now, cancel_point );
  }

  public update( now: number ): void
  {
    if ( this.last_update_time === null )
      this.last_update_time = now;

    const dt_ms           = Math.max( 0, now - this.last_update_time );
    this.last_update_time = now;

    this.updateBasic( now );

    if ( this.is_raf_animating ) return;
    if ( dt_ms < 1 ) return;

    if (
      this.options.enable_momentum &&
      !this.is_scrolling &&
      this.touch_start_pos === null &&
      Math.abs( this.scroll_velocity ) > 0.001
    )
    {
      const momentum_delta   = this.scroll_velocity * dt_ms * this.options.scroll_speed;
      const previous_offset  = this.scroll_offset;
      const adjusted_delta   = this.applyRubberBandEffect( momentum_delta );
      this.updateScrollOffset( this.scroll_offset + adjusted_delta );

      const hit_top    = this.scroll_offset <= 0 && this.scroll_velocity < 0;
      const hit_bottom = this.scroll_offset >= this.max_scroll_offset && this.scroll_velocity > 0;
      const no_move    = this.scroll_offset === previous_offset;

      if ( hit_top || hit_bottom || no_move )
      {
        this.scroll_velocity = 0;
        return;
      }

      const frame_ratio    = dt_ms / 16.6667;
      const friction       = Math.exp( this.log_friction * frame_ratio );
      this.scroll_velocity *= friction;

      if ( Math.abs( this.scroll_velocity ) < 0.02 )
        this.scroll_velocity = 0;
    }
  }

  public render(): void
  {
    /* content lives on secondary CSS canvas — nothing drawn on main canvas,
       but the dirty flag must still be consumed or the parent re-renders forever */
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "UIScrollableContainerCSS";
  }
}
